import { Component, ReactNode, ErrorInfo } from 'react';
import { Link } from 'react-router-dom';
import classes from './ErrorBoundary.module.scss';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={classes.errorBoundary}>
          <div className={classes.container}>
            <div className={classes.icon}>💥</div>
            <h1 className={classes.title}>Oops!</h1>
            <h2 className={classes.subtitle}>Something went wrong</h2>
            <p className={classes.message}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <div className={classes.actions}>
              <button onClick={this.handleReset} className={classes.button}>
                Try Again
              </button>
              <Link to="/" className={`${classes.button} ${classes.secondary}`}>
                Go Home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
